import { useState, useEffect, useMemo } from "react";
import { BookOpen, Loader2, Search, ArrowLeft } from "lucide-react";
import { fetchPokedex } from "../lib/api";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";

// Vue Pokédex : combien d'ESPÈCES différentes la collection couvre, en tout et
// par région. Une espèce est acquise dès qu'une carte la représentant est
// possédée, peu importe la variante ou l'extension — c'est un Pokédex, pas un
// compte de cartes.
//
// Le lien carte -> espèce vient du `dexId` de TCGdex, donc les Dresseurs et les
// Énergies n'y figurent jamais. Les noms d'espèces sont déduits des cartes déjà
// en cache (le nom le plus court rencontré pour un numéro).

// Sprites : fichiers statiques du dépôt public PokeAPI/sprites (~1 Ko pièce,
// taille idéale pour une grille de 1025 cases). L'URL se construit directement
// depuis le numéro national, donc rien à mettre en cache côté backend.
const spriteUrl = (dexId) =>
  `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${dexId}.png`;

// `region`/`onRegionChange` sont levés dans App.js : un aller-retour vers
// "Explorer les cartes" démonte ce composant, et une région gardée en state
// local serait perdue au remontage (retour à tort sur l'écran de sélection).
export const PokedexView = ({ onSearchSpecies, region, onRegionChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchPokedex()
      .then(setData)
      .catch(err => console.error("Pokédex :", err))
      .finally(() => setLoading(false));
  }, []);

  const ownedSet = useMemo(() => new Set(data?.owned_dex || []), [data]);
  const names = useMemo(() => data?.names || {}, [data]);

  // Espèces affichées : celles de la région ouverte, ou le résultat de la
  // recherche (qui, elle, cherche dans tout le national).
  const entries = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const inRange = (n) => (q ? true : region && n >= region.from && n <= region.to);
    const out = [];
    for (let n = 1; n <= data.national.total; n++) {
      if (!inRange(n)) continue;
      const name = names[String(n)] || "";
      if (q && !name.toLowerCase().includes(q) && String(n) !== q) continue;
      out.push({ n, name });
    }
    return out;
  }, [data, region, query, names]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;
  }
  if (!data) {
    return <p className="text-zinc-500 py-10 text-center">Pokédex indisponible.</p>;
  }

  const pct = (owned, total) => (total > 0 ? Math.round(owned / total * 100) : 0);
  const showList = !!region || !!query.trim();

  return (
    // `main-content` a deja son propre padding : en ajouter un ici mangeait
    // assez de largeur sur telephone pour couter une colonne de la grille.
    <div className="px-0 py-2 sm:p-6">
      <div className="flex items-center gap-3 mb-1">
        {showList && (
          <button onClick={() => { onRegionChange?.(null); setQuery(""); }}
            className="text-zinc-400 hover:text-white" title="Retour aux régions">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <BookOpen className="w-6 h-6 text-red-400" />
        <h2 className="text-2xl font-bold text-white">
          {region ? `Pokédex — ${region.label}` : "Pokédex"}
        </h2>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        Une espèce compte dès qu'une de ses cartes est possédée, toutes extensions confondues.
      </p>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input placeholder="Rechercher un Pokémon par nom ou n°…" className="search-input pl-10"
          value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {!showList ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[{ key: "national", label: "National", from: 1, to: data.national.total,
              total: data.national.total, owned: data.national.owned },
            ...data.regions,
          ].map(r => (
            <button key={r.key} onClick={() => onRegionChange?.(r)}
              className="set-completion-card text-left">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-white font-semibold">{r.label}</span>
                <span className="text-sm text-zinc-400">
                  <span className="text-white font-semibold">{r.owned}</span>
                  <span className="text-zinc-600">/{r.total}</span>
                  <span className="ml-2 text-emerald-400">{pct(r.owned, r.total)}%</span>
                </span>
              </div>
              <Progress value={pct(r.owned, r.total)} className="h-2 bg-zinc-800" />
            </button>
          ))}
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-500 mb-3">
            {entries.filter(e => ownedSet.has(e.n)).length} / {entries.length} possédés
          </p>
          <div className="pokedex-grid">
            {entries.map(({ n, name }) => {
              const owned = ownedSet.has(n);
              return (
                <button key={n}
                  onClick={() => name && onSearchSpecies?.(name)}
                  title={name ? `Voir les cartes de ${name}` : "Espèce jamais rencontrée dans les sets parcourus"}
                  className={`pokedex-cell ${owned ? "pokedex-owned" : ""}`}>
                  {/* Une espèce non possédée reste en silhouette : on voit d'un
                      coup d'œil ce qui manque. Sprite absent -> case vide. */}
                  <img src={spriteUrl(n)} alt="" loading="lazy"
                    className={`pokedex-sprite ${owned ? "" : "pokedex-sprite-missing"}`}
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  <span className="pokedex-num">N°{String(n).padStart(4, "0")}</span>
                  <span className="pokedex-name">{name || "—"}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
